import {
  ApplicationActionTypes,
  Actions,
} from '../_actions/application.actions';
import {
  ApplicationData,
  ApplicationTemplatePayload,
  UserApplication,
  UserEmail,
} from '../_models/application.models';
import { Error } from '../_models/auth.models';
import { Application } from '../_models/company.model';
import { Operations, OpTypes } from '../_models/general.model';
import { Datatable } from '../_models/report.models';
import { ApplicationMetric } from '../_models/settings.models';
import { Template } from '../_models/template.model';

/**
 * State interface
 */
export interface ApplicationState {
  templates: {
    [appId: string]: Array<ApplicationTemplatePayload>
  }
  appNames: { [key: string]: string };
  appMetrics: { [applicationId: string]: ApplicationMetric };
  ipTrackingDatatable: {
    [applicationId: string]: Datatable;
  };
  applications: Array<UserApplication> | null;
  applicationsData: {
    [key: string]: ApplicationData;
  };
  emails: {
    [applicationId: string]: Array<UserEmail>;
  };
  domainStatus:{
    [applicationId:string]:any;
  };
  createSuccess:{
    [applicationId:string]:any;
  };
  timeZoneData:any;
  languagesData:any;
  lastUpdatedApplicationId: string | null;
  usersDatatable: {
    [applicationId: string]: Datatable;
  };
  error: Error | null;
  createError: Error | null;
  operations: Operations;
  applicationById: {[applicationId: string]: any};
  isApplicationsLoading:{[key:string]:boolean};
  emailsLoading:{[key:string]:boolean};
  templatesLoading:{[key:string]:boolean};
  timeZoneDataLoading:{[key:string]:boolean};
  languagesDataLoading:{[key:string]:boolean};
  subdomains: any[];
  subdomainsLoading: { isLoading: boolean; isError: boolean };

}

/**
 * Initials State
 */
export const initialState: ApplicationState = {
  applications: null,
  ipTrackingDatatable: {},
  appMetrics: {},
  applicationsData: {},
  usersDatatable: {},
  error: null,
  emails: {},
  createError: null,
  operations: {},
  appNames: {},
  domainStatus:{},
  createSuccess:{},
  lastUpdatedApplicationId: null,
  applicationById: {},
  templates: {},
  timeZoneData: [],
  languagesData: [],
  isApplicationsLoading:{},
  emailsLoading:{},
  templatesLoading:{},
  timeZoneDataLoading:{isLoading:false, isError: false},
  languagesDataLoading:{isLoading:false, isError: false},
  subdomains: [],
  subdomainsLoading: { isLoading: false, isError: false },
};

/**
 * Reducer
 */
export const ApplicationReducer = (
  state = initialState,
  action: Actions
): ApplicationState => {
  switch (action.type) {
    case ApplicationActionTypes.GET_TIME_ZONES:
      return{
        ...state,
        timeZoneDataLoading:{isLoading: true, isError: false}
      }
    case ApplicationActionTypes.GET_LANGUAGES:{
      return{
        ...state,
        languagesDataLoading:{isLoading: true, isError: false},
      }
    }
    case ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.CREATE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.UPDATE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES_SUCCESS:
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.appId]: action.payload,
        },
        error: null,
        templatesLoading:{...state.templatesLoading,[action.appId]:false,[`error-${action.appId}`]:false}
      };
    case ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES_SUCCESS:
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.appId]: action.payload,
        },
        error: null,
        templatesLoading:{...state.templatesLoading, [action.appId]:false, [`error-${action.appId}`]:false}
      };
    case ApplicationActionTypes.CHECK_DOMAIN_STATUS_SUCCESS:
      return {
        ...state,
        domainStatus:{
          ...state.domainStatus,
          [action.appId]:action.payload,
        },
        operations:{
          ...state.operations,
          [action.refId]: {
            type: OpTypes.CHECKED,
            error: null,
          },
        }
      };
    case ApplicationActionTypes.CREATE_DOMAIN_SUCCESS:
      return {
        ...state,
        createSuccess:{
          ...state.createSuccess,
          [action.appId]:action.payload,
        }
      };
    case ApplicationActionTypes.LOAD_USER_APPLICATIONS_SUCCESS:
      let object: any = {};
      for (let app of action.payload) object[app.Key] = app.Name;
      return {
        ...state,
        applications: action.payload,
        appNames: {
          ...state.appNames,
          ...object,
        },
        error: null,
      };
    case ApplicationActionTypes.GET_APP_METRICS_SUCCESS:
      return {
        ...state,
        appMetrics: {
          ...state.appMetrics,
          [action.applicationId]: action.payload,
        },
      };
    case ApplicationActionTypes.CREATE_APPLICATION_SUCCESS:
      return {
        ...state,
        applications: [...(state.applications || []), action.payload],
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.CREATE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.LOAD_APPLICATION_SUCCESS:
      return {
        ...state,
        applicationsData: {
          ...state.applicationsData,
          [action.tableId]: action.payload,
        },
        appNames: {
          ...state.appNames,
          [action.tableId]: action.payload.Name,
        },
        isApplicationsLoading:{...state.isApplicationsLoading,[action.tableId]:false, [`error-${action.tableId}`]:false}
      };
    case ApplicationActionTypes.GET_TIME_ZONES_SUCCESS:
      return {
        ...state,
        timeZoneData: [
          ...action.payload,
        ],
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.LOAD,
            payload: action.payload,
            error: null,
          },
        },
        timeZoneDataLoading:{isLoading:false, isError: false}
      };
    case ApplicationActionTypes.GET_LANGUAGES_SUCCESS:
      return {
        ...state,
        languagesData: [
          ...action.payload,
        ],
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.LOAD,
            payload: action.payload,
            error: null,
          },
        },
        languagesDataLoading:{isLoading: false, isError: false}
      };
    case ApplicationActionTypes.LOAD_EMAILS_SUCCESS:
      return {
        ...state,
        emails: {
          ...state.emails,
          [action.applicationId]: action.payload,
        },
        emailsLoading:{...state.emailsLoading,[action.applicationId]:false, [`error-${action.applicationId}`]:false}

      };
    case ApplicationActionTypes.ADD_EMAIL_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.CREATE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE_SUCCESS:
      return {
        ...state,
        ipTrackingDatatable: {
          ...state.ipTrackingDatatable,
          [action.applicationId]: action.payload,
        },
      };
    case ApplicationActionTypes.LOAD_APPLICATION_BY_ID_SUCCESS:
      let currentApps = [...(state.applications || [])];
      const currIndex = currentApps.findIndex(
        (app) => app.Key === action.payload.Key
      );

      if (currIndex > -1) currentApps[currIndex] = action.payload;
      else currentApps.push(action.payload);
      currentApps.sort((a, b) => a.Name.localeCompare(b.Name));
      return {
        ...state,
        applicationById: {
          ...state.applicationById,
          [action.appId]: action.payload,
        },
        applications: currentApps,
        appNames: {
          ...state.appNames,
          [action.payload.Key]: action.payload.Name,
        },
      };
    case ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE_SUCCESS:
      return {
        ...state,
        usersDatatable: {
          ...state.usersDatatable,
          [action.applicationId]: action.payload,
        },
      };
    case ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE_SUCCESS:
      const delIndex = state.templates[action.appId]?.findIndex(
        (template) => template.Key === action.key
      );
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.appId]: [
            ...state.templates[action.appId].slice(0, delIndex),
            ...state.templates[action.appId].slice(delIndex + 1),
          ],
        },
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.DELETE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.DELETE_APPLICATION_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.DELETE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.RENAME_APPLICATION_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.UPDATE,
            error: null,
          },
        },
      };
    case ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE:
    case ApplicationActionTypes.CREATE_APPLICATION:
    case ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE:
    case ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE:
    case ApplicationActionTypes.LOAD_USER_APPLICATIONS:
    case ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE:
    case ApplicationActionTypes.LOAD_APPLICATION_TEMPLATE:
    case ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE:
    case ApplicationActionTypes.DELETE_APPLICATION:
    case ApplicationActionTypes.RENAME_APPLICATION:
      return {
        ...state,
        error: null,
      };
    case ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES:
      return{
        ...state,
        error: null,
        templatesLoading:{...state.templatesLoading, [action.appId]:true, [`error-${action.appId}`]:false}

      }
    case ApplicationActionTypes.LOAD_EMAILS:
      return{
        ...state,
        error: null,
        emailsLoading:{...state.emailsLoading,[action.applicationId]:true, [`error-${action.applicationId}`]:false}

      }
    case ApplicationActionTypes.LOAD_APPLICATION:
      return {
        ...state,
        error: null,
        isApplicationsLoading:{...state.isApplicationsLoading,[action.tableId]: true,[`error-${action.tableId}`]:false},

      };
    case ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES:
      return {
        ...state,
        error: null,
        templatesLoading:{...state.templatesLoading,[action.appId]: true, [`error-${action.appId}`]:false},
      };
    case ApplicationActionTypes.CREATE_APPLICATION_FAILED:
    case ApplicationActionTypes.ADD_EMAIL_FAILED:
    case ApplicationActionTypes.DELETE_APPLICATION_TEMPLATE_FAILED:
    case ApplicationActionTypes.DELETE_APPLICATION_FAILED:
    case ApplicationActionTypes.RENAME_APPLICATION_FAILED:
    case ApplicationActionTypes.CREATE_APPLICATION_TEMPLATE_FAILED:
      return {
        ...state,
        error: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
      };
    case ApplicationActionTypes.GET_LANGUAGES_FAILED:
      return{
        ...state,
        error: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
        languagesDataLoading:{isLoading: false, isError: true},
      }
    case ApplicationActionTypes.GET_TIME_ZONES_FAILED:
      return{
        ...state,
        error: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
        timeZoneDataLoading:{isLoading: false, isError: true}
      }
    case ApplicationActionTypes.CREATE_DOMAIN_FAILED:
    case ApplicationActionTypes.CHECK_DOMAIN_STATUS_FAILED:
      return {
        ...state,
        error: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
      };
    // case ApplicationActionTypes.LOAD_EMAILS_FAILED:
    case ApplicationActionTypes.LOAD_APPLICATION_FAILED:
      return{
        ...state,
        error: action.payload,
        lastUpdatedApplicationId: null,
        isApplicationsLoading:{...state.isApplicationsLoading, [action.tableId]:false, [`error-${action.tableId}`]:true}
      }
    case ApplicationActionTypes.LOAD_USER_APPLICATIONS_FAILED:
    case ApplicationActionTypes.UPDATE_APPLICATION_FAILED:
    case ApplicationActionTypes.LOAD_APPLICATION_BY_ID_FAILED:
    case ApplicationActionTypes.LOAD_IP_TRACKING_DATATABLE_FAILED:
    case ApplicationActionTypes.LOAD_APPLICATIONS_USERS_DATATABLE_FAILED:
    case ApplicationActionTypes.LOAD_APPLICATION_TEMPLATE_FAILED:
    case ApplicationActionTypes.UPDATE_APPLICATION_TEMPLATE_FAILED:
      return {
        ...state,
        error: action.payload,
        lastUpdatedApplicationId: null,
      };
    case ApplicationActionTypes.LOAD_APPLICATION_TEMPLATES_FAILED:
      return{
        ...state,
        error: action.payload,
        lastUpdatedApplicationId: null,
        templatesLoading:{...state.templatesLoading, [action.appId]:false, [`error-${action.appId}`]:true}
      }
    case ApplicationActionTypes.LOAD_EMAILS_FAILED:
      return{
        ...state,
        error: action.payload,
        lastUpdatedApplicationId: null,
        emailsLoading:{...state.emailsLoading,[action.applicationId]:false, [`error-${action.applicationId}`]:true}
      }

    case ApplicationActionTypes.LOAD_APPLICATION_DOC_TEMPLATES_FAILED:
      return {
        ...state,
        error: action.payload,
        lastUpdatedApplicationId: null,
        templatesLoading:{...state.templatesLoading,[action.appId]:false,[`error-${action.appId}`]:true}
      };
    case ApplicationActionTypes.UPDATE_APPLICATION_SUCCESS:
      let index = (state.applications || []).findIndex(
        (items: UserApplication) => {
          if (action.payload != null) return action.payload.Key == items.Key;
          else return false;
        }
      );

      let spliceFromApplications = [...(state.applications || [])];
      if (index > -1) {
        spliceFromApplications[index] = action.payload;
      }

      return {
        ...state,
        applications: spliceFromApplications,
        lastUpdatedApplicationId: action.payload.Key,
      };
    case ApplicationActionTypes.CLEAR_APPS:
      return {
        ...state,
        applications: null,
      };
    case ApplicationActionTypes.UPDATE_APPLICATION:
      return {
        ...state,
        lastUpdatedApplicationId: null,
      };
      case ApplicationActionTypes.LOAD_SUBDOMAINS_FAILED:
        return {
          ...state,
          error: action.payload,
          subdomainsLoading: { isLoading: false, isError: true },
        };
      case ApplicationActionTypes.LOAD_SUBDOMAINS:
        return {
          ...state,
          subdomainsLoading: { isLoading: true, isError: false },
          error: null,
        };
      case ApplicationActionTypes.LOAD_SUBDOMAINS_SUCCESS:
        return {
          ...state,
          subdomains: action.payload,
          subdomainsLoading: { isLoading: false, isError: false },
        };
    case ApplicationActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
};
